import React, { useState, useEffect, useCallback } from "react";
import { FilterProto, ColumnInfo, Header, ObservationColumnProto } from "build-ts/metadata";
import { BenchmarkType, FilterOperator, AggregationType, aggregationTypeFromJSON } from "build-ts/enums";
import { BenchmarkEntityProto, BenchmarkEntityProto_DefinitionMapping } from "build-ts/knowledege_graph";
import * as MetricService from "../../../services/MetricService";
import * as DataModelService from "../../../services/DataModelService";
import showToast from "../../../hooks/useCustomToast";
import { DateFilter, DatePeriodEnum } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";
import { ModelInfo } from "build-ts/metadata";
import { useConfigurationContext } from "context/ConfigurationContext";
import BenchmarkEditForm from "modules/benchmark/components/BenchmarkEditForm";

interface FilterWithValues {
  column: Header;
  values: string[];
  operator: FilterOperator;
}

interface MetricDefinitionMapping {
  mapping: BenchmarkEntityProto_DefinitionMapping;
  metricDetails?: Header;
  isLoading: boolean;
}

interface BenchmarkEditFormContainerProps {
  benchmark: BenchmarkEntityProto;
  onClose: () => void;
  onSave?: () => void;
  triggerSubmit?: boolean;
  setTriggerSubmit?: (value: boolean) => void;
  onReloadKnowledgeGraph?: () => void;
  compact?: boolean;
}

const BenchmarkEditFormContainer: React.FC<BenchmarkEditFormContainerProps> = ({
  benchmark,
  onClose,
  onSave,
  triggerSubmit,
  setTriggerSubmit,
  onReloadKnowledgeGraph,
  compact,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [benchmarkName, setBenchmarkName] = useState<string>("");
  const [benchmarkDescription, setBenchmarkDescription] = useState<string>("");
  const [benchmarkType, setBenchmarkType] = useState<BenchmarkType | undefined>(undefined);
  const [selectedGranularity, setSelectedGranularity] = useState<DatePeriodEnum | undefined>(undefined);

  // Versus benchmark filter state (KG column-based)
  const [selectedKGColumn, setSelectedKGColumn] = useState<Header | null>(null);
  const [columnValues, setColumnValues] = useState<string[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isValuesLoading, setIsValuesLoading] = useState<boolean>(false);
  const [showFilterSelector, setShowFilterSelector] = useState<boolean>(false);
  const [filters, setFilters] = useState<FilterWithValues[]>([]);
  const [editFilter, setEditFilter] = useState<boolean>(false);
  const [editingFilterIndex, setEditingFilterIndex] = useState<number>(-1);
  const [editingFilterValues, setEditingFilterValues] = useState<string[]>([]);

  // Target benchmark definition mapping state
  const [definitionMappings, setDefinitionMappings] = useState<MetricDefinitionMapping[]>([]);
  const [showDefinitionModal, setShowDefinitionModal] = useState(false);
  const [availableMetrics, setAvailableMetrics] = useState<Header[]>([]);
  const [selectedMetricForDefinition, setSelectedMetricForDefinition] = useState<string>("");
  const [targetDataModelHeaders, setTargetDataModelHeaders] = useState<Header[]>([]);
  const [selectedTargetModelId, setSelectedTargetModelId] = useState<string>("");
  const [targetModelInfo, setTargetModelInfo] = useState<ModelInfo | null>(null);
  const [isLoadingTargetModel, setIsLoadingTargetModel] = useState<boolean>(false);
  const [isSavingDefinition, setIsSavingDefinition] = useState<boolean>(false);

  // Observation panel state (for ObservationContainer)
  const [selectedObservationColumn, setSelectedObservationColumn] = useState<Header | null>(null);
  const [selectedAggregation, setSelectedAggregation] = useState<AggregationType | undefined>(AggregationType.SUM);
  const [selectedObservationColumns, setSelectedObservationColumns] = useState<ObservationColumnProto[]>([]);

  // Filter panel state (for FiltersContainer within definition mapping)
  const [filterColumnValues, setFilterColumnValues] = useState<string[]>([]);
  const [selectedFilterColumn, setSelectedFilterColumn] = useState<ColumnInfo | null>(null);
  const [selectedOperator, setSelectedOperator] = useState<FilterOperator | undefined>(FilterOperator.EQ);
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedColumnValues, setSelectedColumnValues] = useState<string[]>([]);
  const [selectedFilterColumns, setSelectedFilterColumns] = useState<FilterProto[]>([]);
  const [dateFilter, setDateFilter] = useState<DateFilter | undefined>(undefined);
  const [dateFilterCategory, setDateFilterCategory] = useState<DateFilterCategory>(DateFilterCategory.FIXED);

  const { fetchKgColumns, kgColumns } = useConfigurationContext();

  useEffect(() => {
    setIsLoading(true);
    fetchKgColumns();
    setIsLoading(false);
  }, []);

  // Fetch metric details for each definition mapping when benchmark loads
  useEffect(() => {
    if (benchmark?.benchmark_type === BenchmarkType.TARGET_BENCHMARK && benchmark.definition_mapping.length > 0) {
      const initialMappings = benchmark.definition_mapping.map(mapping => ({
        mapping,
        isLoading: true,
      }));
      setDefinitionMappings(initialMappings);

      benchmark.definition_mapping.forEach(async (mapping, index) => {
        if (mapping.metric_id) {
          try {
            const metricDetails = await MetricService.getMetricById(mapping.metric_id, false);
            if (metricDetails) {
              setDefinitionMappings(prevMappings => {
                const newMappings = [...prevMappings];
                newMappings[index] = {
                  ...newMappings[index],
                  metricDetails: metricDetails.header,
                  isLoading: false,
                };
                return newMappings;
              });
            }
          } catch (error) {
            console.error("Error fetching metric details:", error);
            setDefinitionMappings(prevMappings => {
              const newMappings = [...prevMappings];
              newMappings[index] = { ...newMappings[index], isLoading: false };
              return newMappings;
            });
          }
        }
      });
    } else {
      setDefinitionMappings([]);
    }
  }, [benchmark]);

  // Fetch KG column values when selected KG column changes (versus benchmark)
  useEffect(() => {
    if (selectedKGColumn?.id) {
      fetchKGColumnValues();
    } else {
      setColumnValues([]);
      setSelectedValues([]);
    }
  }, [selectedKGColumn]);

  // Populate form from benchmark when it loads
  useEffect(() => {
    if (benchmark) {
      setBenchmarkName(benchmark.header?.name || "");
      setBenchmarkDescription(benchmark.header?.description || "");
      setBenchmarkType(benchmark.benchmark_type);
      setSelectedGranularity(benchmark.granularity);

      if (benchmark.benchmark_type === BenchmarkType.VERSUS_BENCHMARK && benchmark.versus_filters.length > 0) {
        const extractedFilters: FilterWithValues[] = benchmark.versus_filters
          .filter(filter => filter.kg_column_header)
          .map(filter => ({
            column: filter.kg_column_header!,
            values: filter.values,
            operator: filter.operator || FilterOperator.EQ,
          }));
        setFilters(extractedFilters);
      } else {
        setFilters([]);
      }

      resetFilterSelector();
    }
  }, [benchmark]);

  // Fetch available metrics and target models when definition modal opens
  useEffect(() => {
    if (!showDefinitionModal) {
      resetDefinitionForm();
      return;
    }

    const fetchAvailableMetrics = async () => {
      try {
        const metrics = await MetricService.listMetrics("");
        if (metrics) setAvailableMetrics(metrics);
      } catch {
        showToast("Error fetching metrics", "", "error");
      }
    };

    const fetchTargetModels = async () => {
      try {
        const models = await DataModelService.getDataModelHeaders(false);
        if (models) setTargetDataModelHeaders(models);
      } catch {
        showToast("Error fetching target models", "", "error");
      }
    };

    fetchAvailableMetrics();
    fetchTargetModels();
  }, [showDefinitionModal]);

  // Respond to external triggerSubmit
  useEffect(() => {
    if (triggerSubmit) {
      handleSaveBenchmark();
      setTriggerSubmit?.(false);
    }
  }, [triggerSubmit]);

  const fetchKGColumnValues = async () => {
    setIsValuesLoading(true);
    try {
      if (selectedKGColumn?.id) {
        const values = await DataModelService.getKGColumnValues(selectedKGColumn.id);
        setColumnValues(values ?? []);
      }
    } catch {
      showToast("Error fetching column values", "", "error");
    } finally {
      setIsValuesLoading(false);
    }
  };

  const resetFilterSelector = useCallback(() => {
    setSelectedKGColumn(null);
    setSelectedValues([]);
    setShowFilterSelector(false);
  }, []);

  const handleToggleValue = useCallback((value: string) => {
    setSelectedValues(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  }, []);

  const handleAddFilter = useCallback(() => {
    if (!selectedKGColumn || selectedValues.length === 0) {
      showToast("Please select a KG column and at least one value", "", "error");
      return;
    }
    setFilters(prev => [...prev, { column: selectedKGColumn, values: selectedValues, operator: FilterOperator.EQ }]);
    resetFilterSelector();
  }, [selectedKGColumn, selectedValues, resetFilterSelector]);

  const handleRemoveFilter = useCallback((index: number) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleEditFilter = useCallback(
    (filterIndex: number) => {
      const filter = filters[filterIndex];
      if (filter) {
        setEditingFilterIndex(filterIndex);
        setEditingFilterValues([...filter.values]);
        setEditFilter(true);
        setSelectedKGColumn(filter.column);
      }
    },
    [filters]
  );

  const handleSaveEditedFilter = useCallback(() => {
    if (editingFilterIndex >= 0 && editingFilterValues.length > 0) {
      setFilters(prev => {
        const updated = [...prev];
        updated[editingFilterIndex] = { ...updated[editingFilterIndex], values: editingFilterValues };
        return updated;
      });
      handleCancelEditFilter();
      showToast("Filter updated successfully", "", "success");
    }
  }, [editingFilterIndex, editingFilterValues]);

  const handleCancelEditFilter = useCallback(() => {
    setEditFilter(false);
    setEditingFilterIndex(-1);
    setEditingFilterValues([]);
    setSelectedKGColumn(null);
    setColumnValues([]);
    setSelectedValues([]);
  }, []);

  const handleToggleEditValue = useCallback((value: string) => {
    setEditingFilterValues(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  }, []);

  const handleTargetModelSelect = useCallback(async (id: string) => {
    setSelectedTargetModelId(id);
    if (!id) {
      setTargetModelInfo(null);
      return;
    }
    setIsLoadingTargetModel(true);
    try {
      const modelInfo = await DataModelService.getModelById(id);
      if (modelInfo) setTargetModelInfo(modelInfo);
    } catch {
      showToast("Error fetching target model info", "", "error");
    } finally {
      setIsLoadingTargetModel(false);
    }
  }, []);

  const resetDefinitionForm = useCallback(() => {
    setSelectedMetricForDefinition("");
    setSelectedTargetModelId("");
    setTargetModelInfo(null);
    setSelectedObservationColumn(null);
    setSelectedFilterColumn(null);
    setSelectedAggregation(AggregationType.SUM);
    setSelectedOperator(FilterOperator.EQ);
    setInputValue("");
    setFilterColumnValues([]);
    setSelectedColumnValues([]);
    setSelectedFilterColumns([]);
    setSelectedObservationColumns([]);
    setDateFilter(undefined);
    setDateFilterCategory(DateFilterCategory.FIXED);
  }, []);

  const handleObservationColumnChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const columnId = event.target.value;
      const column = targetModelInfo?.columns?.find(c => c.header?.id === columnId);
      setSelectedObservationColumn(column?.header || null);
    },
    [targetModelInfo]
  );

  const handleAggregationChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAggregation(aggregationTypeFromJSON(event.target.value));
  }, []);

  const handleAddObservationColumn = useCallback(() => {
    if (selectedObservationColumn && selectedAggregation) {
      const newColumn: ObservationColumnProto = {
        logical_column_header: selectedObservationColumn,
        aggregation: selectedAggregation,
      };
      setSelectedObservationColumns(prev => [...prev, newColumn]);
      setSelectedObservationColumn(null);
      setSelectedAggregation(undefined);
    } else {
      showToast("Please select all fields.", "", "error");
    }
  }, [selectedObservationColumn, selectedAggregation]);

  const handleDeleteObservationColumn = useCallback((indexToDelete: number) => {
    setSelectedObservationColumns(prev => prev.filter((_, i) => i !== indexToDelete));
  }, []);

  const handleSaveDefinitionMapping = useCallback(async () => {
    if (!benchmark || !selectedMetricForDefinition || !selectedTargetModelId) {
      showToast("Please select a metric and target model", "", "error");
      return;
    }
    if (selectedObservationColumns.length === 0) {
      showToast("Please add at least one observation column", "", "error");
      return;
    }

    setIsSavingDefinition(true);
    try {
      const newMapping = BenchmarkEntityProto_DefinitionMapping.create({
        metric_id: selectedMetricForDefinition,
        benchmark_definition: {
          content: {
            observation_columns: selectedObservationColumns,
            filters: selectedFilterColumns,
          },
        },
      });

      const metricDetails = await MetricService.getMetricById(selectedMetricForDefinition, false);

      const existingIndex = definitionMappings.findIndex(m => m.mapping.metric_id === selectedMetricForDefinition);

      if (existingIndex >= 0) {
        setDefinitionMappings(prev => {
          const updated = [...prev];
          updated[existingIndex] = { mapping: newMapping, metricDetails: metricDetails?.header, isLoading: false };
          return updated;
        });
        showToast("Definition mapping updated", "", "success");
      } else {
        setDefinitionMappings(prev => [
          ...prev,
          { mapping: newMapping, metricDetails: metricDetails?.header, isLoading: false },
        ]);
        showToast("Definition mapping added", "", "success");
      }

      setShowDefinitionModal(false);
    } catch {
      showToast("Error adding definition mapping", "", "error");
    } finally {
      setIsSavingDefinition(false);
    }
  }, [
    benchmark,
    selectedMetricForDefinition,
    selectedTargetModelId,
    selectedObservationColumns,
    selectedFilterColumns,
    definitionMappings,
  ]);

  const handleRemoveDefinitionMapping = useCallback(
    async (index: number) => {
      if (!benchmark) return;
      setIsSaving(true);
      try {
        const updatedBenchmark = { ...benchmark };
        const newMappings = [...updatedBenchmark.definition_mapping];
        newMappings.splice(index, 1);
        updatedBenchmark.definition_mapping = newMappings;

        const savedBenchmark = await MetricService.saveBenchmark(updatedBenchmark);
        if (savedBenchmark) {
          showToast("Definition mapping removed successfully", "", "success");
          setDefinitionMappings(prev => prev.filter((_, i) => i !== index));
        }
      } catch {
        showToast("Error removing definition mapping", "", "error");
      } finally {
        setIsSaving(false);
      }
    },
    [benchmark]
  );

  const handleSaveBenchmark = useCallback(async () => {
    if (!benchmark) return;
    if (!benchmarkName.trim()) {
      showToast("Benchmark name is required", "", "error");
      return;
    }
    if (benchmarkType === BenchmarkType.VERSUS_BENCHMARK && filters.length === 0) {
      showToast("Please add at least one filter", "", "error");
      return;
    }
    if (benchmarkType === BenchmarkType.TARGET_BENCHMARK && definitionMappings.length === 0) {
      showToast("Please add at least one definition for the target benchmark", "", "error");
      return;
    }

    setIsSaving(true);
    try {
      const updatedBenchmark = { ...benchmark };

      if (updatedBenchmark.header) {
        updatedBenchmark.header.name = benchmarkName;
        updatedBenchmark.header.description = benchmarkDescription;
      }
      updatedBenchmark.benchmark_type = benchmarkType;
      updatedBenchmark.granularity = benchmarkType === BenchmarkType.TARGET_BENCHMARK ? selectedGranularity : undefined;

      if (benchmarkType === BenchmarkType.VERSUS_BENCHMARK) {
        updatedBenchmark.versus_filters = filters.map(filter =>
          FilterProto.create({
            kg_column_header: filter.column,
            operator: filter.operator,
            values: filter.values,
            calculated_date_ranges: [],
          })
        );
      } else {
        updatedBenchmark.versus_filters = [];
      }

      if (benchmarkType === BenchmarkType.TARGET_BENCHMARK) {
        updatedBenchmark.definition_mapping = definitionMappings.map(item => item.mapping);
      } else {
        updatedBenchmark.definition_mapping = [];
      }

      const savedBenchmark = await MetricService.saveBenchmark(updatedBenchmark);
      if (savedBenchmark) {
        showToast("Benchmark updated successfully", "", "success");
        onSave?.();
        onClose();
        onReloadKnowledgeGraph?.();
      }
    } catch {
      showToast("Error updating benchmark", "", "error");
    } finally {
      setIsSaving(false);
    }
  }, [
    benchmark,
    benchmarkName,
    benchmarkDescription,
    benchmarkType,
    selectedGranularity,
    filters,
    definitionMappings,
    onSave,
    onClose,
    onReloadKnowledgeGraph,
  ]);

  return (
    <BenchmarkEditForm
      compact={compact}
      onClose={onClose}
      benchmark={benchmark}
      isLoading={isLoading}
      isSaving={isSaving}
      benchmarkName={benchmarkName}
      setBenchmarkName={setBenchmarkName}
      benchmarkDescription={benchmarkDescription}
      setBenchmarkDescription={setBenchmarkDescription}
      benchmarkType={benchmarkType}
      selectedGranularity={selectedGranularity}
      setSelectedGranularity={setSelectedGranularity}
      // Versus benchmark
      filters={filters}
      kgColumns={kgColumns ?? undefined}
      selectedKGColumn={selectedKGColumn}
      setSelectedKGColumn={setSelectedKGColumn}
      columnValues={columnValues}
      selectedValues={selectedValues}
      isValuesLoading={isValuesLoading}
      showFilterSelector={showFilterSelector}
      setShowFilterSelector={setShowFilterSelector}
      editFilter={editFilter}
      editingFilterIndex={editingFilterIndex}
      editingFilterValues={editingFilterValues}
      handleToggleValue={handleToggleValue}
      handleToggleEditValue={handleToggleEditValue}
      handleAddFilter={handleAddFilter}
      handleRemoveFilter={handleRemoveFilter}
      handleEditFilter={handleEditFilter}
      handleSaveEditedFilter={handleSaveEditedFilter}
      handleCancelEditFilter={handleCancelEditFilter}
      resetFilterSelector={resetFilterSelector}
      // Target benchmark definition mappings
      definitionMappings={definitionMappings}
      showDefinitionModal={showDefinitionModal}
      setShowDefinitionModal={setShowDefinitionModal}
      availableMetrics={availableMetrics}
      selectedMetricForDefinition={selectedMetricForDefinition}
      setSelectedMetricForDefinition={setSelectedMetricForDefinition}
      targetDataModelHeaders={targetDataModelHeaders}
      selectedTargetModelId={selectedTargetModelId}
      handleTargetModelSelect={handleTargetModelSelect}
      targetModelInfo={targetModelInfo}
      isLoadingTargetModel={isLoadingTargetModel}
      isSavingDefinition={isSavingDefinition}
      handleSaveDefinitionMapping={handleSaveDefinitionMapping}
      handleRemoveDefinitionMapping={handleRemoveDefinitionMapping}
      // Observation panel
      selectedObservationColumn={selectedObservationColumn}
      selectedAggregation={selectedAggregation}
      selectedObservationColumns={selectedObservationColumns}
      handleObservationColumnChange={handleObservationColumnChange}
      handleAggregationChange={handleAggregationChange}
      handleAddObservationColumn={handleAddObservationColumn}
      handleDeleteObservationColumn={handleDeleteObservationColumn}
      // Filter panel (FiltersContainer within definition mapping)
      filterColumnValues={filterColumnValues}
      setFilterColumnValues={setFilterColumnValues}
      selectedFilterColumn={selectedFilterColumn}
      setSelectedFilterColumn={setSelectedFilterColumn}
      selectedOperator={selectedOperator}
      setSelectedOperator={setSelectedOperator}
      inputValue={inputValue}
      setInputValue={setInputValue}
      selectedColumnValues={selectedColumnValues}
      setSelectedColumnValues={setSelectedColumnValues}
      selectedFilterColumns={selectedFilterColumns}
      setSelectedFilterColumns={setSelectedFilterColumns}
      dateFilter={dateFilter}
      setDateFilter={setDateFilter}
      dateFilterCategory={dateFilterCategory}
      setDateFilterCategory={setDateFilterCategory}
    />
  );
};

export default BenchmarkEditFormContainer;
